import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CtaButton } from "@/components/common/Cta";
import { Field, inputCls, Honeypot, isEmail, Success } from "./fields";
import { submitForm, errorMessage } from "@/lib/api";
import { ROLES, CONTACT } from "@/data/site";

const blank = (role) => ({ name: "", phone: "", email: "", role, years_experience: "", safe_pass: null, message: "", website: "" });

const YesNo = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[[true, "Yes"], [false, "No"]].map(([v, l]) => (
      <button type="button" key={l} onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`application-safepass-${l.toLowerCase()}`}
        className={`h-12 flex-1 border font-display text-base font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${value === v ? "border-brand bg-brand text-canvas" : "border-canvas-line text-ink hover:border-brand"}`}>
        {l}
      </button>
    ))}
  </div>
);

export const ApplicationForm = ({ defaultRole }) => {
  const [form, setForm] = useState(blank(defaultRole));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.phone.trim()) errs.phone = "Please enter a phone number.";
    if (!isEmail(form.email)) errs.email = "Please enter a valid email.";
    if (form.safe_pass === null) errs.safe_pass = "Please choose yes or no.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await submitForm("applications", form);
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Success title="Application received" testid="application-success">
        <p>Thanks {form.name.split(" ")[0]} — your application for <span className="text-ink">{form.role}</span> has been sent to our recruitment team.</p>
        <p>To complete it, please email your CV to <a className="text-brand underline" href={`mailto:${CONTACT.recruitmentEmail}?subject=${encodeURIComponent(`CV – ${form.role}`)}`}>{CONTACT.recruitmentEmail}</a>, quoting the role.</p>
      </Success>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="application-form">
      <Honeypot value={form.website} onChange={set("website")} />
      <Field label="Role" htmlFor="application-role" className="sm:col-span-2" required>
        <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
          <SelectTrigger id="application-role" className={inputCls} data-testid="application-role-select"><SelectValue /></SelectTrigger>
          <SelectContent className="z-[200] rounded-none border-canvas-line bg-canvas-800 text-ink">
            {ROLES.map((r) => <SelectItem key={r.id} value={r.title} data-testid={`application-role-${r.id}`}>{r.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Name" htmlFor="application-name" error={errors.name} required>
        <Input id="application-name" value={form.name} onChange={set("name")} className={inputCls} data-testid="application-name-input" />
      </Field>
      <Field label="Phone" htmlFor="application-phone" error={errors.phone} required>
        <Input id="application-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="application-phone-input" />
      </Field>
      <Field label="Email" htmlFor="application-email" error={errors.email} required>
        <Input id="application-email" type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="application-email-input" />
      </Field>
      <Field label="Years of experience" htmlFor="application-years">
        <Input id="application-years" inputMode="numeric" value={form.years_experience} onChange={set("years_experience")} className={inputCls} data-testid="application-years-input" />
      </Field>
      <Field label="Safe Pass & Manual Handling?" htmlFor="application-safepass" error={errors.safe_pass} required className="sm:col-span-2">
        <YesNo value={form.safe_pass} onChange={(v) => setForm((f) => ({ ...f, safe_pass: v }))} />
      </Field>
      <Field label="Short message" htmlFor="application-message" className="sm:col-span-2">
        <Textarea id="application-message" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} h-auto min-h-[110px] py-3`} data-testid="application-message-input" />
      </Field>
      <div className="sm:col-span-2">
        <CtaButton type="submit" disabled={loading} testid="application-submit-btn" className="w-full sm:w-auto">{loading ? "Sending…" : "Submit application"}</CtaButton>
      </div>
    </form>
  );
};
